import type { AccountSettings, WebOnboardingHandoff } from "@um/contracts";

function validId(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value);
}

export function isWebHandoff(value: unknown): value is WebOnboardingHandoff {
  if (!value || typeof value !== "object") return false;
  const item = value as WebOnboardingHandoff;
  return (
    validId(item.accountId) &&
    validId(item.characterId) &&
    ["pending", "resumed", "complete"].includes(item.status) &&
    (!item.sessionId || item.sessionId === webHandoffId(item))
  );
}

export function webHandoffId(
  handoff: Pick<WebOnboardingHandoff, "accountId" | "characterId">
): string {
  return `${handoff.accountId}:${handoff.characterId}`;
}

/** Old apps finished by clearing progress. Preserve that compatibility only for old markers. */
export function webHandoffIsComplete(
  handoff: WebOnboardingHandoff,
  settings: AccountSettings | null | undefined
): boolean {
  return (
    handoff.status === "complete" ||
    (!handoff.completionVersion &&
      settings?.createdDefaultCharacter === true &&
      !settings.onboardingProgress)
  );
}

/** A local routing guard, not authorization to select another account. */
export function accountWebHandoff(
  accountId: string | null | undefined,
  uid: string | undefined,
  settings: AccountSettings | null | undefined,
  userHandoff?: WebOnboardingHandoff
): WebOnboardingHandoff | undefined {
  const handoff = settings?.webOnboardingHandoff || userHandoff;
  return uid &&
    isWebHandoff(handoff) &&
    handoff.accountId === accountId &&
    (!handoff.userId || handoff.userId === uid)
    ? handoff
    : undefined;
}

function acknowledgementKey(
  uid: string,
  handoff: WebOnboardingHandoff
): string {
  return `um.webOnboardingHandled:${uid}:${webHandoffId(handoff)}`;
}
const acknowledged = new Set<string>();

export function hasHandledWebHandoff(
  uid: string,
  handoff: WebOnboardingHandoff
): boolean {
  const key = acknowledgementKey(uid, handoff);
  try {
    return acknowledged.has(key) || localStorage.getItem(key) === "1";
  } catch {
    return acknowledged.has(key);
  }
}

export function acknowledgeWebHandoff(
  uid: string,
  handoff: WebOnboardingHandoff
): void {
  const key = acknowledgementKey(uid, handoff);
  acknowledged.add(key);
  try {
    localStorage.setItem(key, "1");
  } catch {
    /* In-memory acknowledgement still prevents loops. */
  }
}

/** IDs are routing hints only. The service verifies membership and saved origin. */
export function webHandoffIntent(
  search: string
): WebOnboardingHandoff | undefined {
  const params = new URLSearchParams(search);
  const accountId = params.get("webOnboardingAccount");
  const characterId = params.get("webOnboardingCharacter");
  if (!validId(accountId) || !validId(characterId)) return;
  return { accountId, characterId, status: "pending" };
}

export function webHandoffPath(handoff: WebOnboardingHandoff): string {
  const params = new URLSearchParams({
    webOnboardingAccount: handoff.accountId,
    webOnboardingCharacter: handoff.characterId,
  });
  if (handoff.minimumAppVersion)
    params.set("minVersion", handoff.minimumAppVersion);
  return `/onboarding?${params}`;
}

export const WEB_HANDOFF_LINK_EVENT = "um:web-onboarding-link";
const intentKey = "um.webOnboardingIntent";
let linkIntent: WebOnboardingHandoff | undefined;

export function queuedWebHandoff(): WebOnboardingHandoff | undefined {
  if (linkIntent) return linkIntent;
  try {
    const value = JSON.parse(sessionStorage.getItem(intentKey) || "null");
    return isWebHandoff(value) ? value : undefined;
  } catch {
    return;
  }
}

export function clearWebHandoffIntent(): void {
  linkIntent = undefined;
  try {
    sessionStorage.removeItem(intentKey);
  } catch {
    /* Storage can be unavailable. */
  }
}

/** Only recognizes our handoff route, including the OneLink deep_link_value wrapper. */
export function captureWebHandoffLink(value: unknown): boolean {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value, "https://app.uniqueminds.com");
    const nested = url.searchParams.get("deep_link_value");
    const destination = nested ? new URL(nested, url.origin) : url;
    if (destination.pathname !== "/onboarding") return false;
    const intent = webHandoffIntent(destination.search);
    if (!intent) return false;
    linkIntent = intent;
    try {
      sessionStorage.setItem(intentKey, JSON.stringify(intent));
    } catch {
      /* Keep the in-memory intent. */
    }
    window.dispatchEvent(new Event(WEB_HANDOFF_LINK_EVENT));
    return true;
  } catch {
    return false;
  }
}
